import {
  useGlobalSearchParams,
  useNavigationContainerRef,
  useSegments,
} from 'expo-router';
import { TabList, TabSlot, Tabs, TabTrigger } from 'expo-router/ui';
import { type ComponentProps, useEffect } from 'react';
import { Hide, mq, StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import useRootStore from '@/stores/useRootStore';
import Box from '../UI/Box';
import type Icon from '../Unistyles/Icon';
import * as BottomTabs from './BottomTabs';
import { GetOrganizationDetailLayout } from './ContextNavigationFragments';
import Drawer from './Drawer';

type ContextTabConfig = {
  name: 'organization' | 'team';
  label: string;
  activeIcon: ComponentProps<typeof Icon>['name'];
  inactiveIcon: ComponentProps<typeof Icon>['name'];
  badge?: number | null;
};

type TabConfig = BottomTabs.DefaultTabConfig | ContextTabConfig;

const displayedTabItems = ['event', 'chat'] as const;

const GetTeamTab = graphql(`
  query GetTeamTabFragment($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      ...TeamTab
    }
  }
`, [BottomTabs.TeamTabFragment]);

function useSyncContextFromParams() {
  const segments = useSegments();
  const navigationRef: {
    getCurrentRoute(): { params?: object } | undefined;
  } = useNavigationContainerRef();
  const isTabRoute = segments.some((segment) => segment === '(tabs)');

  const filteredSegments = segments.filter((segment) => {
    return !['(tabs)', '(app)', '(private)'].includes(segment);
  });

  const baseSegment = filteredSegments[0];

  const { organizationId, teamId } = useGlobalSearchParams<{
    organizationId?: string;
    teamId?: string;
  }>();

  useEffect(() => {
    if (!isTabRoute) {
      return;
    }

    if (baseSegment === 'event') {
      useRootStore.setState({
        activeTab: 'event',
      });
      return;
    }

    const params = navigationRef.getCurrentRoute()?.params;
    const activeOrganizationId =
      params && 'organizationId' in params ? organizationId : undefined;
    const activeTeamId = params && 'teamId' in params ? teamId : undefined;

    if (baseSegment === 'organization') {
      useRootStore.setState({
        activeTab: 'organization',
        activeContextPathname: '/organization',
        ...(typeof activeOrganizationId === 'string'
          ? { organizationId: activeOrganizationId }
          : {}),
      });
      return;
    }

    if (baseSegment === 'team') {
      useRootStore.setState({
        activeTab: 'team',
        activeContextPathname: '/team',
        ...(typeof activeTeamId === 'string' ? { teamId: activeTeamId } : {}),
      });
      return;
    }

    if (baseSegment === 'chat') {
      useRootStore.setState({
        activeTab: 'chat',
      });
      return;
    }
  }, [organizationId, teamId, baseSegment, isTabRoute, navigationRef]);
}

export default function ResponsiveTabNavigator({
  unseenMessageCount,
}: {
  unseenMessageCount: number | null;
}) {
  useSyncContextFromParams();
  const personId = useUserId();

  const tabConfig: TabConfig[] = [
    {
      name: 'event',
      activeIcon: 'calendar',
      inactiveIcon: 'calendar-outline',
      label: 'Events',
      badge: null,
      testID: 'BottomEventTab',
    },
    {
      name: 'chat',
      activeIcon: 'chatbubble',
      inactiveIcon: 'chatbubble-outline',
      label: 'Messages',
      badge: unseenMessageCount,
      testID: 'BottomChatTab',
    },
    {
      name: 'organization',
      activeIcon: 'people',
      inactiveIcon: 'people-outline',
      label: 'Organizations',
    },
    {
      name: 'team',
      activeIcon: 'people',
      inactiveIcon: 'people-outline',
      label: 'Teams',
    },
  ];

  const displayedTabs = tabConfig.filter(
    (tab): tab is BottomTabs.DefaultTabConfig =>
      displayedTabItems.some(
        (displayedTabItem) => displayedTabItem === tab.name,
      ),
  );

  const [organizationId, teamId, activeContextPathname, activeTab] =
    useRootStore((state) => [
      state.organizationId,
      state.teamId,
      state.activeContextPathname,
      state.activeTab,
    ]);

  const isTeamContextFocused =
    activeTab === 'team' && activeContextPathname === '/team';
  const isOrganizationContextFocused =
    activeTab === 'organization' && activeContextPathname === '/organization';

  const tabHrefs: Record<
    TabConfig['name'],
    ComponentProps<typeof TabTrigger>['href']
  > = {
    event: '/event',
    chat: '/chat',
    organization: organizationId
      ? {
          pathname: '/organization',
          params: { organizationId },
        }
      : '/organization',
    team: teamId
      ? {
          pathname: '/team',
          params: { teamId },
        }
      : '/team',
  };

  const [{ data: organizationData }] = useQuery({
    query: GetOrganizationDetailLayout,
    variables: {
      organizationId: organizationId || '',
      personId,
    },
    pause: !organizationId,
  });

  const [{ data: teamData }] = useQuery({
    query: GetTeamTab,
    variables: {
      teamId: teamId!,
    },
    pause: !teamId,
  });
  const drawerTabs = displayedTabs.map((tab) => ({
    activeIcon: tab.activeIcon,
    inactiveIcon: tab.inactiveIcon,
    label: tab.label,
    name: tab.name,
    ...(tab.badge ? { badge: tab.badge } : {}),
  }));

  return (
    <Tabs asChild>
      <TabList>
        {tabConfig.map((tab) => (
          <TabTrigger
            key={tab.name}
            name={tab.name}
            href={tabHrefs[tab.name]}
          />
        ))}
      </TabList>
      <Drawer tabs={drawerTabs}>
        <Box flex>
          <TabSlot detachInactiveScreens style={style.tabSlot} />
          <Hide mq={mq.only.width('md')}>
            <BottomTabs.default>
              {teamId && activeContextPathname === '/team' ? (
                <TabTrigger name="team" href={tabHrefs.team} asChild>
                  <BottomTabs.TeamTab
                    fragment={teamData?.team_by_pk}
                    isContextFocused={isTeamContextFocused}
                  />
                </TabTrigger>
              ) : null}

              {organizationId && activeContextPathname === '/organization' ? (
                <TabTrigger
                  name="organization"
                  href={tabHrefs.organization}
                  asChild
                >
                  <BottomTabs.OrganizationTab
                    fragment={organizationData?.event_by_pk}
                    isContextFocused={isOrganizationContextFocused}
                  />
                </TabTrigger>
              ) : null}

              {displayedTabs.map((tab) => (
                <TabTrigger
                  key={tab.name}
                  name={tab.name}
                  href={tabHrefs[tab.name]}
                  asChild
                >
                  <BottomTabs.DefaultTab tab={tab} />
                </TabTrigger>
              ))}
            </BottomTabs.default>
          </Hide>
        </Box>
      </Drawer>
    </Tabs>
  );
}

const style = StyleSheet.create(() => ({
  tabSlot: {
    flex: 1,
  },
}));
